import { useCallback } from "react";
import cc from "classcat";
import {
  weekdaysToRecurrenceRule,
  recurrenceRuleToWeekdays,
} from "byzantine/src/dbbl/utils";

import styles from "./MultiWeekdaySelector.module.scss";

type Day = {
  label: string;
  value: number;
  ariaLabel: string;
};

interface MultiWeekdaySelectorProps {
  onChange?: (selectedDays: string) => void;
  selectedDays?: string;
}

const days: Day[] = [
  { label: "M", value: 0, ariaLabel: "Monday" },
  { label: "T", value: 1, ariaLabel: "Tuesday" },
  { label: "W", value: 2, ariaLabel: "Wednesday" },
  { label: "T", value: 3, ariaLabel: "Thursday" },
  { label: "F", value: 4, ariaLabel: "Friday" },
  { label: "S", value: 5, ariaLabel: "Saturday" },
  { label: "S", value: 6, ariaLabel: "Sunday" },
];

export const updateSelectedDays = ({
  dayValue,
  selectedDays,
}: {
  dayValue: number;
  selectedDays: number[];
}) => {
  const isSelected = selectedDays.includes(dayValue);
  let updatedSelected: number[];

  // Prevent removing the last selected day.
  if (selectedDays.length <= 1 && isSelected) {
    updatedSelected = selectedDays;
  } else if (selectedDays.length > 1 && isSelected) {
    updatedSelected = selectedDays.filter((value) => value !== dayValue);
  } else {
    // Add that day. <3
    updatedSelected = [...selectedDays, dayValue];
  }

  return updatedSelected;
};

const MultiWeekdaySelector = ({
  onChange,
  selectedDays = "",
}: MultiWeekdaySelectorProps) => {
  const derivedSelectedDays = recurrenceRuleToWeekdays(selectedDays) || [];

  const handleDayClick = useCallback(
    (dayValue: number) => {
      if (onChange) {
        const updatedSelected = updateSelectedDays({
          selectedDays: derivedSelectedDays,
          dayValue,
        });

        onChange(weekdaysToRecurrenceRule(updatedSelected) || "");
      }
    },
    [derivedSelectedDays, onChange]
  );

  return (
    <div className={styles.container}>
      {days.map((day) => {
        const isSelected = derivedSelectedDays.includes(day.value);
        return (
          <button
            key={day.value}
            type="button"
            className={cc(["button--reset", styles.dayToggle])}
            aria-pressed={isSelected}
            aria-label={day.ariaLabel}
            onClick={() => handleDayClick(day.value)}
          >
            {day.label}
          </button>
        );
      })}
    </div>
  );
};

export default MultiWeekdaySelector;
